import React from 'react';
import { t } from '../../lib/i18n';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Progress } from '../ui/progress';
import { Separator } from '../ui/separator';
import {
  Shield, Target, AlertTriangle, Brain, Eye, EyeOff,
  TrendingUp, TrendingDown, Minus, Crosshair, Lightbulb,
  Scale, Zap, Search, Lock, Unlock
} from 'lucide-react';

// Tactic severity colors
const tacticColors = {
  low: 'border-blue-400/30 text-blue-400 bg-blue-400/10',
  medium: 'border-amber-400/30 text-amber-400 bg-amber-400/10',
  high: 'border-red-400/30 text-red-400 bg-red-400/10'
};

const tacticIcons = {
  ANCHORING: Crosshair,
  BLUFFING: Eye,
  TIME_PRESSURE: Zap,
  SALAMI: Scale,
  BATNA_REFERENCE: Shield,
  EMOTIONAL_APPEAL: Brain,
  GOOD_COP_BAD_COP: Target,
  FLINCHING: AlertTriangle,
  NIBBLING: Search,
  REFRAMING: Lightbulb
};

export function TacticsPanel({ tactics, lang = 'pl' }) {
  if (!tactics) return null;

  const detected = tactics.detected || [];
  const bluff = tactics.bluff_assessment || {};
  const phase = tactics.negotiation_phase || 'exploration';
  const style = tactics.counterparty_style || 'unknown';

  const phaseLabels = {
    pl: { opening: 'Otwarcie', exploration: 'Eksploracja', bargaining: 'Targowanie', closing: 'Zamykanie', deadlock: 'Impas' },
    en: { opening: 'Opening', exploration: 'Exploration', bargaining: 'Bargaining', closing: 'Closing', deadlock: 'Deadlock' }
  };

  const phaseColors = {
    opening: 'bg-blue-400/10 text-blue-400 border-blue-400/20',
    exploration: 'bg-primary/10 text-primary border-primary/20',
    bargaining: 'bg-amber-400/10 text-amber-400 border-amber-400/20',
    closing: 'bg-emerald-400/10 text-emerald-400 border-emerald-400/20',
    deadlock: 'bg-red-400/10 text-red-400 border-red-400/20'
  };

  return (
    <div className="space-y-4" data-testid="tactics-panel">
      {/* Negotiation Phase */}
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">
          {lang === 'pl' ? 'Faza negocjacji' : 'Negotiation Phase'}
        </span>
        <Badge variant="outline" className={`text-xs ${phaseColors[phase] || ''}`}>
          {phaseLabels[lang]?.[phase] || phase}
        </Badge>
      </div>

      {/* Bluff Meter */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <Eye className="w-3 h-3" />
            {lang === 'pl' ? 'Prawdopodobieństwo blefu' : 'Bluff Probability'}
          </span>
          <span className="text-xs font-mono font-medium" style={{
            color: (bluff.overall_bluff_probability || 0) > 0.5 ? '#E05252' :
              (bluff.overall_bluff_probability || 0) > 0.3 ? '#D8B07A' : '#22C7A3'
          }}>
            {Math.round((bluff.overall_bluff_probability || 0) * 100)}%
          </span>
        </div>
        <Progress
          value={(bluff.overall_bluff_probability || 0) * 100}
          className="h-1.5"
        />
      </div>

      <Separator />

      {/* Detected Tactics */}
      {detected.length > 0 ? (
        <div>
          <span className="text-xs font-medium text-muted-foreground mb-2 block">
            {lang === 'pl' ? 'Wykryte taktyki' : 'Detected Tactics'} ({detected.length})
          </span>
          <div className="space-y-2">
            {detected.map((tactic, i) => {
              const TacticIcon = tacticIcons[tactic.tactic] || AlertTriangle;
              return (
                <div key={i} className={`p-2 rounded-lg border ${tacticColors[tactic.severity] || tacticColors.medium}`}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <TacticIcon className="w-3 h-3 flex-shrink-0" />
                    <span className="text-xs font-medium">{tactic.tactic}</span>
                    <span className="text-xs opacity-60 ml-auto">{Math.round((tactic.confidence || 0) * 100)}%</span>
                  </div>
                  <p className="text-xs opacity-80">{tactic.evidence}</p>
                  {tactic.recommended_counter && (
                    <p className="text-xs opacity-60 mt-1 italic">
                      {lang === 'pl' ? 'Kontra' : 'Counter'}: {tactic.recommended_counter}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground text-center py-3">
          {lang === 'pl' ? 'Brak wykrytych taktyk' : 'No tactics detected'}
        </p>
      )}

      {/* Specific Bluffs */}
      {bluff.specific_bluffs && bluff.specific_bluffs.length > 0 && (
        <>
          <Separator />
          <div>
            <span className="text-xs font-medium text-red-400 mb-2 block flex items-center gap-1">
              <Eye className="w-3 h-3" />
              {lang === 'pl' ? 'Podejrzane twierdzenia' : 'Suspected Bluffs'}
            </span>
            <div className="space-y-1.5">
              {bluff.specific_bluffs.map((b, i) => (
                <div key={i} className="p-2 rounded-lg bg-red-400/5 border border-red-400/10 text-xs">
                  <span className="font-medium">"{b.claim}"</span>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-muted-foreground">{b.reason}</span>
                    <Badge variant="outline" className="text-xs border-red-400/30 text-red-400">
                      {Math.round((b.bluff_probability || 0) * 100)}%
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}


export function StrategyPanel({ strategy, zopa, lang = 'pl' }) {
  if (!strategy && !zopa) return null;

  const strategyLabels = {
    pl: {
      build_rapport: 'Budowanie relacji',
      probe_for_info: 'Zbieranie informacji',
      counter_anchor: 'Kontra-kotwica',
      make_concession: 'Koncesja',
      stand_firm: 'Twarde stanowisko',
      reframe: 'Przeramowanie',
      close_deal: 'Zamykanie transakcji',
      walk_away_threat: 'Groźba odejścia',
      standard: 'Standardowa'
    },
    en: {
      build_rapport: 'Build Rapport',
      probe_for_info: 'Probe for Info',
      counter_anchor: 'Counter-Anchor',
      make_concession: 'Make Concession',
      stand_firm: 'Stand Firm',
      reframe: 'Reframe',
      close_deal: 'Close Deal',
      walk_away_threat: 'Walk Away Threat',
      standard: 'Standard'
    }
  };

  return (
    <div className="space-y-4" data-testid="strategy-panel">
      {/* Current Strategy */}
      {strategy && (
        <>
          <div className="p-3 rounded-lg bg-primary/5 border border-primary/20">
            <div className="flex items-center gap-1.5 mb-1">
              <Target className="w-3.5 h-3.5 text-primary" />
              <span className="text-xs font-medium text-primary">
                {lang === 'pl' ? 'Strategia' : 'Strategy'}
              </span>
            </div>
            <span className="text-sm font-medium">
              {strategyLabels[lang]?.[strategy.strategy] || strategy.strategy}
            </span>
            {strategy.turn_goal && (
              <p className="text-xs text-muted-foreground mt-1">{strategy.turn_goal}</p>
            )}
          </div>

          {/* Information Strategy */}
          {strategy.information_strategy && (
            <div className="space-y-2">
              {strategy.information_strategy.reveal?.length > 0 && (
                <div className="flex items-start gap-1.5">
                  <Unlock className="w-3 h-3 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="text-xs text-emerald-400 font-medium">
                      {lang === 'pl' ? 'Ujawnić' : 'Reveal'}
                    </span>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {strategy.information_strategy.reveal.map((r, i) => (
                        <p key={i}>- {r}</p>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {strategy.information_strategy.hide?.length > 0 && (
                <div className="flex items-start gap-1.5">
                  <Lock className="w-3 h-3 text-red-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="text-xs text-red-400 font-medium">
                      {lang === 'pl' ? 'Ukryć' : 'Hide'}
                    </span>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {strategy.information_strategy.hide.map((h, i) => (
                        <p key={i}>- {h}</p>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {strategy.information_strategy.probe?.length > 0 && (
                <div className="flex items-start gap-1.5">
                  <Search className="w-3 h-3 text-blue-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="text-xs text-blue-400 font-medium">
                      {lang === 'pl' ? 'Zbadać' : 'Probe'}
                    </span>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {strategy.information_strategy.probe.map((p, i) => (
                        <p key={i}>- {p}</p>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Risk */}
          {strategy.risk_assessment && strategy.risk_assessment !== 'low' && (
            <div className={`p-2 rounded-lg border ${
              strategy.risk_assessment === 'high' ? 'border-red-400/20 bg-red-400/5' : 'border-amber-400/20 bg-amber-400/5'
            }`}>
              <div className="flex items-center gap-1.5">
                <AlertTriangle className={`w-3 h-3 ${strategy.risk_assessment === 'high' ? 'text-red-400' : 'text-amber-400'}`} />
                <span className={`text-xs font-medium ${strategy.risk_assessment === 'high' ? 'text-red-400' : 'text-amber-400'}`}>
                  {lang === 'pl' ? 'Ryzyko' : 'Risk'}: {strategy.risk_assessment}
                </span>
              </div>
              {strategy.risk_details && (
                <p className="text-xs text-muted-foreground mt-1">{strategy.risk_details}</p>
              )}
            </div>
          )}
        </>
      )}

      {/* ZOPA Estimate */}
      {zopa && zopa.our_ideal && (
        <>
          <Separator />
          <div>
            <span className="text-xs font-medium text-muted-foreground mb-2 block flex items-center gap-1">
              <Scale className="w-3 h-3" />
              ZOPA ({lang === 'pl' ? 'Strefa porozumienia' : 'Zone of Possible Agreement'})
            </span>
            <div className="space-y-1.5 text-xs">
              <div className="flex justify-between">
                <span className="text-emerald-400">{lang === 'pl' ? 'Nasz ideał' : 'Our ideal'}</span>
                <span>{zopa.our_ideal}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-amber-400">{lang === 'pl' ? 'Nasze minimum' : 'Our minimum'}</span>
                <span>{zopa.our_minimum}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-red-400">{lang === 'pl' ? 'Ich minimum (est.)' : 'Their min (est.)'}</span>
                <span>{zopa.their_likely_minimum}</span>
              </div>
              <div className="flex items-center justify-between mt-1 pt-1 border-t border-border">
                <span className="text-muted-foreground">{lang === 'pl' ? 'Porozumienie możliwe' : 'Deal possible'}</span>
                <Badge variant="outline" className={`text-xs ${
                  zopa.overlap_exists ? 'border-emerald-400/30 text-emerald-400' : 'border-red-400/30 text-red-400'
                }`}>
                  {zopa.overlap_exists ? (lang === 'pl' ? 'TAK' : 'YES') : (lang === 'pl' ? 'NIE' : 'NO')}
                </Badge>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}


export function MemoryPanel({ memory, lang = 'pl' }) {
  if (!memory) return null;

  return (
    <div className="space-y-3" data-testid="memory-panel">
      {/* Key Facts */}
      {memory.key_facts?.length > 0 && (
        <div>
          <span className="text-xs font-medium text-muted-foreground mb-1.5 block">
            {lang === 'pl' ? 'Kluczowe fakty' : 'Key Facts'}
          </span>
          <div className="space-y-1">
            {memory.key_facts.map((fact, i) => (
              <div key={i} className="flex items-start gap-1.5 text-xs">
                <Lightbulb className="w-3 h-3 text-primary mt-0.5 flex-shrink-0" />
                <span>{fact}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Leverage Points */}
      {memory.leverage_points?.length > 0 && (
        <div>
          <span className="text-xs font-medium text-emerald-400 mb-1.5 block flex items-center gap-1">
            <TrendingUp className="w-3 h-3" />
            {lang === 'pl' ? 'Punkty wpływu' : 'Leverage Points'}
          </span>
          <div className="space-y-1">
            {memory.leverage_points.map((lp, i) => (
              <p key={i} className="text-xs text-muted-foreground pl-4">- {lp}</p>
            ))}
          </div>
        </div>
      )}

      {/* Contradictions */}
      {memory.contradictions?.length > 0 && (
        <div>
          <span className="text-xs font-medium text-red-400 mb-1.5 block flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            {lang === 'pl' ? 'Sprzeczności' : 'Contradictions'}
          </span>
          <div className="space-y-1">
            {memory.contradictions.map((c, i) => (
              <p key={i} className="text-xs text-muted-foreground pl-4">- {c}</p>
            ))}
          </div>
        </div>
      )}

      {/* Promises */}
      {(memory.promises?.by_us?.length > 0 || memory.promises?.by_them?.length > 0) && (
        <div>
          <span className="text-xs font-medium text-muted-foreground mb-1.5 block">
            {lang === 'pl' ? 'Obietnice' : 'Promises'}
          </span>
          {memory.promises?.by_us?.length > 0 && (
            <div className="mb-1.5">
              <span className="text-xs text-primary">{lang === 'pl' ? 'Nasze:' : 'Ours:'}</span>
              {memory.promises.by_us.map((p, i) => (
                <p key={i} className="text-xs text-muted-foreground pl-3">- {p}</p>
              ))}
            </div>
          )}
          {memory.promises?.by_them?.length > 0 && (
            <div>
              <span className="text-xs text-amber-400">{lang === 'pl' ? 'Ich:' : 'Theirs:'}</span>
              {memory.promises.by_them.map((p, i) => (
                <p key={i} className="text-xs text-muted-foreground pl-3">- {p}</p>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Concession Tracker */}
      {(memory.concession_tracker?.our_concessions?.length > 0 || memory.concession_tracker?.their_concessions?.length > 0) && (
        <div>
          <Separator className="my-2" />
          <span className="text-xs font-medium text-muted-foreground mb-1.5 block flex items-center gap-1">
            <Scale className="w-3 h-3" />
            {lang === 'pl' ? 'Śledzenie koncesji' : 'Concession Tracker'}
          </span>
          {memory.concession_tracker?.our_concessions?.map((c, i) => (
            <div key={i} className="text-xs flex items-center gap-1 text-muted-foreground py-0.5">
              <TrendingDown className="w-3 h-3 text-red-400 flex-shrink-0" />
              <span>{lang === 'pl' ? 'My' : 'We'}: {c.concession}</span>
            </div>
          ))}
          {memory.concession_tracker?.their_concessions?.map((c, i) => (
            <div key={i} className="text-xs flex items-center gap-1 text-muted-foreground py-0.5">
              <TrendingUp className="w-3 h-3 text-emerald-400 flex-shrink-0" />
              <span>{lang === 'pl' ? 'Oni' : 'They'}: {c.concession}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
